import React, { useCallback, useMemo, useState } from "react";
import {
  QBodyLayout,
  QBox,
  QButton,
  QButtonGroup,
  QDateMetadataItem,
  QFooter,
  QFormControl,
  QHeader,
  QHorizontalMetadata,
  QInput,
  QSelect,
  QStack,
  QText,
  QTextarea,
  QTitle,
  useToastProvider,
} from "@qualio/ui-components";
import {
  Controller,
  FormProvider,
  useForm,
  DefaultValues,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FMEARiskForm, formToRisk } from "../validation";
import { zodResolver } from "@hookform/resolvers/zod";
import { ResourceComponentField } from "../../../components/ResourceComponentField";
import {
  isRequirementPolicy,
  isRiskPolicy,
  isTestCasePolicy,
} from "@design-controls/types";
import { useCurrentConfigs } from "../../../hooks/useCurrentConfigs";
import { RelateDesignElements } from "../../../components/RelateDesignElements/RelateDesignElements";
import { FormRiskScore } from "./FormRiskScore";
import { useUpdateDesignElement } from "../../../hooks/designElement/useUpdateDesignElement";
import { useNavigate } from "react-router";
import { riskScore } from "../../../domain/fmeaRequiresControl";
import { handleFormError } from "../../../api/handleFormError";

type FormProps = {
  risk: FMEARiskForm;
  onClose: () => void;
};

const containsRiskControl = (risk: FMEARiskForm) =>
  !!(
    risk.mitigatesRequirements?.length ||
    risk.mitigatesTestCases?.length ||
    risk.riskControl?.mitigation
  );

export const Form: React.FC<FormProps> = ({ risk, onClose }) => {
  const { t } = useTranslation();
  const configs = useCurrentConfigs();
  const riskConfig = configs.find(isRiskPolicy);
  const [manualRiskControlEnabled, setManualRiskControlEnabled] =
    useState<boolean>(containsRiskControl(risk));

  const { isLoading, mutate } = useUpdateDesignElement();
  const navigate = useNavigate();
  const { showToast } = useToastProvider();

  const defaultValues = risk as DefaultValues<FMEARiskForm>;
  const methods = useForm<FMEARiskForm>({
    defaultValues,
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: zodResolver(FMEARiskForm),
  });

  const { categoryResourceIdentifier } = useMemo(() => {
    return {
      categoryResourceIdentifier: riskConfig?.category?.[0].resourceIdentifier,
    };
  }, [riskConfig]);

  const requirementTypes = useMemo(() => {
    return configs.filter(isRequirementPolicy).map((item) => item.type);
  }, [configs]);

  const testCaseTypes = useMemo(() => {
    return configs.filter(isTestCasePolicy).map((item) => item.type) as (
      | "testCase1"
      | "testCase2"
      | "testCase3"
    )[];
  }, [configs]);

  const {
    watch,
    setError,
    formState: { errors },
  } = methods;

  const [
    preMitigationSeverity,
    preMitigationOccurence,
    preMitigationDetectability,
  ] = watch([
    "preMitigation.severity",
    "preMitigation.occurrence",
    "preMitigation.detectability",
  ]) as string[];

  const [
    postMitigationSeverity,
    postMitigationOccurence,
    postMitigationDetectability,
  ] = watch([
    "postMitigation.severity",
    "postMitigation.occurrence",
    "postMitigation.detectability",
  ]) as string[];

  const riskControlRequired = useMemo(() => {
    if (!riskConfig) {
      return false;
    }

    const threshold = Number(
      riskConfig.assessment.fmea?.mitigationThreshold ?? 0,
    );

    return (
      (riskScore(
        preMitigationSeverity,
        preMitigationDetectability,
        preMitigationOccurence,
      ) ?? 0) > threshold
    );
  }, [
    riskConfig,
    preMitigationSeverity,
    preMitigationOccurence,
    preMitigationDetectability,
  ]);

  const onSubmit = useCallback(
    (formData: FMEARiskForm) => {
      if (!riskConfig) {
        return;
      }

      const data = formToRisk(formData);
      mutate(data as any, {
        onSuccess: () => navigate(`../risk/fmea/${data.id}`),
        onError: (error) => {
          showToast({
            title: t("risks.fmea.edit.notifications.saveError.title"),
            description: t(
              "risks.fmea.edit.notifications.saveError.description",
            ),
            status: "error",
            "data-cy": "toast-fmea-risk-save-error",
          });
          handleFormError(error, setError);
        },
      });
    },
    [riskConfig, mutate, navigate, setError],
  );

  const maxScaleOptions = useMemo(() => {
    if (!riskConfig?.assessment.fmea?.scaleMaxValue) {
      return [];
    }

    return Array(Number(riskConfig?.assessment.fmea?.scaleMaxValue))
      .fill(1)
      .map((_, index) => ({
        value: (index + 1).toString(10),
        label: (index + 1).toString(10),
      }));
  }, [riskConfig]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} data-cy="edit-fmea-risk">
        <QBodyLayout.Default size="md">
          <QHeader>
            <QTitle>
              <QText as="span" color="gray.500">
                {risk.code}
              </QText>{" "}
              {risk.title}
            </QTitle>
            <QHorizontalMetadata>
              <QDateMetadataItem
                label={t("common.metadata.created")}
                value={new Date(risk.created)}
                withTime={true}
              />
              <QDateMetadataItem
                label={t("common.metadata.lastModified")}
                value={new Date(risk.updated)}
                withTime={true}
              />
            </QHorizontalMetadata>
          </QHeader>
          <QStack gap="16px">
            <QText fontStyle="italic">
              {t("risks.fmea.edit.helpers.requiredFields")}
            </QText>
            <QFormControl
              label={t("risks.fmea.edit.fields.title")}
              isRequired
              isInvalid={!!errors?.title}
              error={errors?.title?.message}
            >
              <Controller
                name="title"
                render={({ field: { value, onChange } }) => (
                  <QInput value={value} onChange={onChange} data-cy="title" />
                )}
              />
            </QFormControl>
            {categoryResourceIdentifier && (
              <ResourceComponentField
                name="category"
                label={t("risks.fmea.edit.fields.category")}
                resourceSubType={categoryResourceIdentifier}
              />
            )}
            <QText fontSize="xl" fontWeight={600}>
              {t("risks.fmea.edit.sections.initialAssessment")}
            </QText>

            <QFormControl
              label={t("risks.fmea.edit.fields.failureMode")}
              isInvalid={!!errors?.failureMode}
              error={errors?.failureMode?.message}
            >
              <Controller
                name="failureMode"
                render={({ field: { value, onChange } }) => (
                  <QTextarea
                    value={value}
                    onChange={onChange}
                    data-cy="failureMode"
                  />
                )}
              />
            </QFormControl>

            <QStack direction="row" columnGap="24px">
              <QBox width="188px">
                <QFormControl
                  label={t("risks.fmea.edit.fields.severity")}
                  isInvalid={!!errors?.preMitigation?.severity}
                  data-cy="premitigation-severity"
                >
                  <Controller
                    name="preMitigation.severity"
                    render={({ field: { value, onChange } }) => (
                      <QSelect
                        value={value}
                        options={maxScaleOptions}
                        onChange={(item) => item && onChange(item.value)}
                      />
                    )}
                  />
                </QFormControl>
              </QBox>
              <QBox width="188px">
                <QFormControl
                  label={t("risks.fmea.edit.fields.occurence")}
                  isInvalid={!!errors?.preMitigation?.occurrence}
                  data-cy="premitigation-occurrence"
                >
                  <Controller
                    name="preMitigation.occurrence"
                    render={({ field: { value, onChange } }) => (
                      <QSelect
                        value={value}
                        options={maxScaleOptions}
                        onChange={(item) => item && onChange(item.value)}
                      />
                    )}
                  />
                </QFormControl>
              </QBox>

              <QBox width="188px">
                <QFormControl
                  label={t("risks.fmea.edit.fields.detectability")}
                  isInvalid={!!errors?.preMitigation?.detectability}
                  data-cy="premitigation-detectability"
                >
                  <Controller
                    name="preMitigation.detectability"
                    render={({ field: { value, onChange } }) => (
                      <QSelect
                        value={value}
                        options={maxScaleOptions}
                        onChange={(item) => item && onChange(item.value)}
                      />
                    )}
                  />
                </QFormControl>
              </QBox>
              <QStack gap="4px" width="103px">
                <QText fontSize="sm" fontWeight={600}>
                  {t("risks.fmea.edit.labels.risk")}
                </QText>
                <QBox>
                  <FormRiskScore
                    severity={preMitigationSeverity}
                    occurence={preMitigationOccurence}
                    detectability={preMitigationDetectability}
                  />
                </QBox>
              </QStack>
              <QStack gap="4px" width="103px">
                <QText fontSize="sm" fontWeight={600}>
                  {t("risks.fmea.edit.labels.riskControl")}
                </QText>
                {riskControlRequired ? (
                  <QText fontSize="sm" fontWeight={600}>
                    {t("risks.fmea.edit.labels.riskControlRequired")}
                  </QText>
                ) : (
                  <QText fontSize="sm">
                    {t("risks.fmea.edit.labels.riskControlNotRequired")}
                  </QText>
                )}
              </QStack>
            </QStack>

            <QFormControl
              label={t("risks.fmea.edit.fields.effectsOfFailureMode")}
              isInvalid={!!errors?.failureModeEffects}
              error={errors?.failureModeEffects?.message}
            >
              <Controller
                name="failureModeEffects"
                render={({ field: { value, onChange } }) => (
                  <QTextarea
                    value={value}
                    onChange={onChange}
                    data-cy="failureModeEffects"
                  />
                )}
              />
            </QFormControl>

            <QFormControl
              label={t("risks.fmea.edit.fields.causesOfFailure")}
              isInvalid={!!errors?.failureCauses}
              error={errors?.failureCauses?.message}
            >
              <Controller
                name="failureCauses"
                render={({ field: { value, onChange } }) => (
                  <QTextarea
                    value={value}
                    onChange={onChange}
                    data-cy="failureCauses"
                  />
                )}
              />
            </QFormControl>

            <QFormControl
              label={t(
                "risks.fmea.edit.fields.currentControlsEvaluationMethod",
              )}
              isInvalid={!!errors?.evaluationMethod}
              error={errors?.evaluationMethod?.message}
            >
              <Controller
                name="evaluationMethod"
                render={({ field: { value, onChange } }) => (
                  <QTextarea
                    value={value}
                    onChange={onChange}
                    data-cy="evaluationMethod"
                  />
                )}
              />
            </QFormControl>

            {requirementTypes.length > 0 && (
              <QFormControl>
                <Controller
                  name="sourceRequirements"
                  render={({ field: { value, onChange } }) => (
                    <RelateDesignElements
                      label={t(
                        "risks.fmea.edit.relatedElements.sourceRequirements",
                      )}
                      resource="dc-requirement"
                      resourceSubType={requirementTypes}
                      onChange={onChange}
                      value={value}
                      data-cy="add-source-requirement"
                      buttonLabel={t(
                        "risks.fmea.edit.relatedElements.addRequirements",
                      )}
                    />
                  )}
                />
              </QFormControl>
            )}

            {testCaseTypes.length > 0 && (
              <QFormControl>
                <Controller
                  name="sourceTestCases"
                  render={({ field: { value, onChange } }) => (
                    <RelateDesignElements
                      label={t(
                        "risks.fmea.edit.relatedElements.sourceTestCases",
                      )}
                      resource="dc-testCase"
                      resourceSubType={testCaseTypes}
                      onChange={onChange}
                      value={value}
                      data-cy="add-source-testcase"
                      buttonLabel={t(
                        "risks.fmea.edit.relatedElements.addTestCases",
                      )}
                    />
                  )}
                />
              </QFormControl>
            )}

            <QStack>
              <QText fontSize="xl" fontWeight={600}>
                {t("risks.fmea.edit.sections.riskControl")}
              </QText>
              <QText>{t("risks.fmea.edit.helpers.riskControlInfo")}</QText>
            </QStack>

            {!manualRiskControlEnabled && !riskControlRequired && (
              <QButton
                variant="outline"
                onClick={() => setManualRiskControlEnabled(true)}
                data-cy="add-risk-control-btn"
              >
                {t("risks.fmea.edit.actions.addRiskControl")}
              </QButton>
            )}

            {(manualRiskControlEnabled || riskControlRequired) && (
              <>
                <QFormControl
                  label={t("risks.fmea.edit.fields.riskControlDescription")}
                  isInvalid={!!errors?.riskControl?.mitigation}
                  error={errors?.riskControl?.mitigation?.message}
                >
                  <Controller
                    name="riskControl.mitigation"
                    render={({ field: { value, onChange } }) => (
                      <QTextarea
                        value={value}
                        onChange={onChange}
                        data-cy="riskControl.mitigation"
                      />
                    )}
                  />
                </QFormControl>

                {requirementTypes.length > 0 && (
                  <QFormControl>
                    <Controller
                      name="mitigatesRequirements"
                      render={({ field: { value, onChange } }) => (
                        <RelateDesignElements
                          label={t(
                            "risks.fmea.edit.relatedElements.riskControlRequirements",
                          )}
                          resource="dc-requirement"
                          resourceSubType={requirementTypes}
                          onChange={onChange}
                          value={value}
                          data-cy="add-mitigation-requirement"
                          buttonLabel={t(
                            "risks.fmea.edit.relatedElements.addRequirements",
                          )}
                        />
                      )}
                    />
                  </QFormControl>
                )}

                {testCaseTypes.length > 0 && (
                  <QFormControl>
                    <Controller
                      name="mitigatesTestCases"
                      render={({ field: { value, onChange } }) => (
                        <RelateDesignElements
                          label={t(
                            "risks.fmea.edit.relatedElements.riskControlTestCases",
                          )}
                          resource="dc-testCase"
                          resourceSubType={testCaseTypes}
                          onChange={onChange}
                          value={value}
                          data-cy="add-mitigation-testcase"
                          buttonLabel={t(
                            "risks.fmea.edit.relatedElements.addTestCases",
                          )}
                        />
                      )}
                    />
                  </QFormControl>
                )}

                <QText fontSize="xl" fontWeight={600}>
                  {t("risks.fmea.edit.sections.finalAssessment")}
                </QText>

                <QStack direction="row" columnGap="24px">
                  <QBox width="188px">
                    <QFormControl
                      label={t("risks.fmea.edit.fields.severity")}
                      isInvalid={!!errors?.postMitigation?.severity}
                      data-cy="postmitigation-severity"
                    >
                      <Controller
                        name="postMitigation.severity"
                        render={({ field: { value, onChange } }) => (
                          <QSelect
                            value={value}
                            options={maxScaleOptions}
                            onChange={(item) => item && onChange(item.value)}
                          />
                        )}
                      />
                    </QFormControl>
                  </QBox>
                  <QBox width="188px">
                    <QFormControl
                      label={t("risks.fmea.edit.fields.occurence")}
                      isInvalid={!!errors?.postMitigation?.occurrence}
                      data-cy="postitigation-occurrence"
                    >
                      <Controller
                        name="postMitigation.occurrence"
                        render={({ field: { value, onChange } }) => (
                          <QSelect
                            value={value}
                            options={maxScaleOptions}
                            onChange={(item) => item && onChange(item.value)}
                          />
                        )}
                      />
                    </QFormControl>
                  </QBox>

                  <QBox width="188px">
                    <QFormControl
                      label={t("risks.fmea.edit.fields.detectability")}
                      isInvalid={!!errors?.postMitigation?.detectability}
                      data-cy="postmitigation-detectability"
                    >
                      <Controller
                        name="postMitigation.detectability"
                        render={({ field: { value, onChange } }) => (
                          <QSelect
                            value={value}
                            options={maxScaleOptions}
                            onChange={(item) => item && onChange(item.value)}
                          />
                        )}
                      />
                    </QFormControl>
                  </QBox>
                  <QStack gap="4px" width="103px">
                    <QText fontSize="sm" fontWeight={600}>
                      {t("risks.fmea.edit.labels.risk")}
                    </QText>
                    <QBox>
                      <FormRiskScore
                        severity={postMitigationSeverity}
                        occurence={postMitigationOccurence}
                        detectability={postMitigationDetectability}
                      />
                    </QBox>
                  </QStack>
                </QStack>
              </>
            )}
          </QStack>

          <QFooter>
            <QButtonGroup>
              <QButton
                variant="outline"
                onClick={onClose}
                data-cy="cancel"
                isDisabled={isLoading}
              >
                {t("risks.fmea.edit.actions.cancel")}
              </QButton>
              <QButton
                type="submit"
                data-cy="save-changes"
                isLoading={isLoading}
              >
                {t("risks.fmea.edit.actions.saveChanges")}
              </QButton>
            </QButtonGroup>
          </QFooter>
        </QBodyLayout.Default>
      </form>
    </FormProvider>
  );
};
